import { useEffect, useMemo, useState, type FormEvent } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Select from '@/components/base/Select';
import Textarea from '@/components/base/Textarea';
import { useToast } from '@/components/feature/ToastProvider';
import { useAuth } from '@/contexts/AuthContext';
import { createReceipt } from '@/lib/receiptService';
import { RECEIPT_CONCEPTS, getCategoriesByType } from '@/lib/categories';
import { todayISO } from '@/lib/format';
import type { Project, Receipt } from '@/types';

interface ReceiptFormProps {
  open: boolean;
  projects: Project[];
  onClose: () => void;
  onCreated: () => void;
}

export default function ReceiptForm({ open, projects, onClose, onCreated }: ReceiptFormProps) {
  const { notify } = useToast();
  const { user, profile } = useAuth();

  const [projectId, setProjectId] = useState('');
  const [receiptDate, setReceiptDate] = useState(todayISO());
  const [concept, setConcept] = useState(RECEIPT_CONCEPTS[0]);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [observation, setObservation] = useState('');
  const [createIncome, setCreateIncome] = useState(true);
  const [incomeCategory, setIncomeCategory] = useState('Mensualidad');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setProjectId('');
      setReceiptDate(todayISO());
      setConcept(RECEIPT_CONCEPTS[0]);
      setDescription('');
      setAmount('');
      setObservation('');
      setCreateIncome(true);
      setIncomeCategory('Mensualidad');
      setErrors({});
      setSaving(false);
    }
  }, [open]);

  const projectOptions = useMemo(
    () => projects.map((project) => ({ value: project.id, label: project.name })),
    [projects],
  );

  const incomeCategories = getCategoriesByType('INGRESO');

  const selectedProject = projects.find((project) => project.id === projectId) ?? null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const next: Record<string, string> = {};
    if (!projectId) next.projectId = 'Seleccione el proyecto asociado.';
    if (!receiptDate) next.receiptDate = 'La fecha es obligatoria.';
    const parsedAmount = Number(amount.replace(',', '.'));
    if (!amount.trim()) next.amount = 'El monto es obligatorio.';
    else if (Number.isNaN(parsedAmount) || parsedAmount <= 0) next.amount = 'El monto debe ser mayor a cero.';

    if (Object.keys(next).length > 0) {
      setErrors(next);
      notify({ tone: 'error', title: 'Revise la información', message: 'Complete los campos obligatorios.' });
      return;
    }

    setSaving(true);
    try {
      await createReceipt({
        projectId,
        projectName: selectedProject?.name ?? 'Proyecto',
        receiptDate,
        concept,
        description,
        amount: parsedAmount,
        observation,
        createIncome,
        incomeCategory,
        createdBy: user?.id ?? null,
        createdByName: profile?.full_name || profile?.username || 'Usuario',
      });
      notify({
        tone: 'success',
        title: 'Recibo generado correctamente.',
        message: 'El recibo fue registrado y ya está disponible para descargar.',
      });
      onCreated();
    } catch {
      notify({
        tone: 'error',
        title: 'No se pudo generar el recibo',
        message: 'Ocurrió un problema al guardar. Intente nuevamente.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      title="Nuevo recibo"
      description="El número de recibo se genera automáticamente con numeración consecutiva."
      icon="ri-receipt-line"
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" form="receipt-form" icon="ri-save-line" loading={saving}>
            Generar recibo
          </Button>
        </>
      }
    >
      <form id="receipt-form" onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <div className="flex items-center gap-3 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
            <i className="ri-hashtag text-base"></i>
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground-900">Número de recibo automático</p>
            <p className="text-xs text-foreground-500">Se asignará el siguiente correlativo (ej. REC-000009).</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            label="* Fecha"
            name="receiptDate"
            type="date"
            value={receiptDate}
            onChange={(event) => {
              setReceiptDate(event.target.value);
              setErrors((prev) => ({ ...prev, receiptDate: '' }));
            }}
            error={errors.receiptDate}
          />
          <Select
            label="* Proyecto / Cliente"
            name="projectId"
            placeholder="Seleccione un proyecto"
            value={projectId}
            onChange={(event) => {
              setProjectId(event.target.value);
              setErrors((prev) => ({ ...prev, projectId: '' }));
            }}
            options={projectOptions}
            error={errors.projectId}
          />
        </div>

        {selectedProject && (
          <div className="grid grid-cols-1 gap-3 rounded-md border border-background-200 bg-background-100/60 p-3.5 sm:grid-cols-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400">Proyecto</p>
              <p className="text-sm font-medium text-foreground-900">{selectedProject.name}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400">Cliente</p>
              <p className="text-sm font-medium text-foreground-900">
                {selectedProject.clients?.name || 'Sin cliente'}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400">Celular</p>
              <p className="text-sm font-medium text-foreground-900">
                {selectedProject.clients?.contact_phone || '—'}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Select
            label="* Concepto"
            name="concept"
            value={concept}
            onChange={(event) => setConcept(event.target.value)}
            options={RECEIPT_CONCEPTS.map((item) => ({ value: item, label: item }))}
          />
          <Input
            label="* Monto (Bs.)"
            name="amount"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              setErrors((prev) => ({ ...prev, amount: '' }));
            }}
            error={errors.amount}
          />
        </div>

        <Textarea
          label="Descripción"
          name="description"
          rows={2}
          maxLength={500}
          showCounter
          placeholder="Detalle del concepto facturado..."
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />

        <Textarea
          label="Observación (opcional)"
          name="observation"
          rows={2}
          maxLength={500}
          showCounter
          placeholder="Notas adicionales del recibo..."
          value={observation}
          onChange={(event) => setObservation(event.target.value)}
        />

        <div className="rounded-md border border-background-200 bg-background-50 p-3.5">
          <label className="flex cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              checked={createIncome}
              onChange={(event) => setCreateIncome(event.target.checked)}
              className="h-4 w-4 cursor-pointer accent-primary-500"
            />
            <span className="text-sm font-medium text-foreground-800">
              Registrar también como ingreso en Finanzas
            </span>
          </label>
          {createIncome && (
            <div className="mt-3">
              <Select
                label="Categoría del ingreso"
                name="incomeCategory"
                value={incomeCategory}
                onChange={(event) => setIncomeCategory(event.target.value)}
                options={incomeCategories.map((item) => ({ value: item, label: item }))}
              />
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}